import { useEffect, useState } from 'react'
import { CClient } from '@common/utils'
import * as Sentry from '@sentry/node'
import {
  SystemChecksToRun,
  SystemCheckResult,
  SystemCheckHookOptionsType,
} from '@lib/types/system-check'
import { useSessionContext } from '@contexts/session'
import {
  checkCameraAndMicAccess,
  checkCookiesSupport,
  checkSupportedBrowser,
  checkSupportedDevice,
  checkSupportedScreenSize,
  checkSupportedUserAgent,
  prepareSystemCheckReqBody,
} from '@lib/utils/system-check'
import {
  getFromLocalStorage,
  setToLocalStorage,
} from '@lib/utils/local-storage'
import { SYSTEM_CHECK_RESULT_LOCAL_STORAGE_KEY } from '@common/constants'
import { SYSTEM_CHECK_RESULT } from '@lib/constants/system-check'
import { getAPIErrorString } from '@lib/utils/common'

const useSystemCheck = (
  systemCheckToRun?: SystemChecksToRun,
  options?: SystemCheckHookOptionsType
) => {
  const { loggedIn, id: userId } = useSessionContext()

  const [isChecking, setIsChecking] = useState(false)
  const [systemCheckResults, setSystemCheckResults] = useState<
    SystemCheckResult
  >({})
  const [didSystemCheckPass, setDidSystemCheckPass] = useState(false)

  const handleSendSystemCheckResult = async (
    currentSystemCheckResults: SystemCheckResult,
    didCurrentSystemPass: boolean
  ) => {
    currentSystemCheckResults.userAgent = checkSupportedUserAgent()
    currentSystemCheckResults.browser = checkSupportedBrowser()

    const body = prepareSystemCheckReqBody(
      currentSystemCheckResults,
      userId,
      options?.classroomId,
      didCurrentSystemPass
    )

    try {
      const url = '/api/v1/system-check-log/'
      const response = await CClient(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      if (!response.ok)
        throw new Error(await getAPIErrorString(response, url, body))
    } catch (error) {
      console.error(error)
      Sentry.captureException(error)
    }
  }

  const runSystemCheck = async () => {
    setIsChecking(true)
    try {
      const currentSystemCheckResults: SystemCheckResult = {}
      if (systemCheckToRun?.checkBrowser) {
        currentSystemCheckResults.browser = checkSupportedBrowser()
        currentSystemCheckResults.browser.passed =
          currentSystemCheckResults.browser.passed &&
          checkSupportedUserAgent().passed
      }
      if (systemCheckToRun?.checkCookies)
        currentSystemCheckResults.cookies = checkCookiesSupport()
      if (systemCheckToRun?.checkScreenSize)
        currentSystemCheckResults.screenSize = checkSupportedScreenSize()
      if (systemCheckToRun?.checkDevice)
        currentSystemCheckResults.device = checkSupportedDevice()
      if (systemCheckToRun?.checkMicrophoneAndCamera) {
        const {
          microphone,
          camera,
          isCameraReadable,
        } = await checkCameraAndMicAccess()
        currentSystemCheckResults.microphone = microphone
        currentSystemCheckResults.camera = camera
        currentSystemCheckResults.isCameraReadable = isCameraReadable
      }

      const didCurrentSystemCheckPass = !Object.entries(
        currentSystemCheckResults
      )
        .map(([, systemCheckResult]) => systemCheckResult.passed)
        .includes(false)

      setSystemCheckResults(currentSystemCheckResults)
      setDidSystemCheckPass(didCurrentSystemCheckPass)

      if (options?.isResultStoredLocally)
        setToLocalStorage(
          SYSTEM_CHECK_RESULT_LOCAL_STORAGE_KEY + userId,
          didCurrentSystemCheckPass
            ? SYSTEM_CHECK_RESULT.PASSED
            : SYSTEM_CHECK_RESULT.FAILED,
          () => Sentry.captureMessage(`Unable to store system check result`)
        )

      if (loggedIn)
        await handleSendSystemCheckResult(
          currentSystemCheckResults,
          didCurrentSystemCheckPass
        )
    } catch (error) {
      Sentry.captureException(error)
      console.error(error)
      // TODO: Remove this once the issue is debugged
      // issue: https://codingal-inc.slack.com/archives/C018W3DKZT9/p1651898336079009
      setDidSystemCheckPass(true)
    }
    setIsChecking(false)
  }

  useEffect(() => {
    options?.isCheckRanAutomatically && runSystemCheck()
  }, [])

  const checkHadSystemCheckRan = () =>
    getFromLocalStorage(SYSTEM_CHECK_RESULT_LOCAL_STORAGE_KEY + userId)

  return {
    systemCheckResults,
    passed: didSystemCheckPass,
    isChecking,
    runSystemCheck,
    checkHadSystemCheckRan,
  }
}

export default useSystemCheck
